import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import TenantDashboardLayout from "@/components/layout/TenantDashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import TenantsPage from "@/pages/TenantsPage";
import BotsPage from "@/pages/BotsPage";
import MasterAIPage from "@/pages/MasterAIPage";
import ConversationsPage from "@/pages/ConversationsPage";
import TicketsPage from "@/pages/TicketsPage";
import ReportsPage from "@/pages/ReportsPage";
import TenantLoginPage from "@/pages/TenantLoginPage";
import TenantDashboardPage from "@/pages/TenantDashboardPage";
import WhatsAppConfigPage from "@/pages/WhatsAppConfigPage";
import ProspectosPage from "@/pages/ProspectosPage";
import BrainFactoryPage from "@/pages/BrainFactoryPage";
import MonitoringPage from "@/pages/MonitoringPage";
import InventoryPage from "@/pages/InventoryPage";
import DataEnginePage from "@/pages/DataEnginePage";
import BlueprintStudioPage from "@/pages/BlueprintStudioPage";
import "@/App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Tenant Route Wrapper
const TenantRouteWrapper = ({ children }) => {
  const { tenantSlug } = useParams();
  return (
    <TenantProvider tenantSlug={tenantSlug}>
      {children}
    </TenantProvider>
  );
};

// Protected Tenant Route
const ProtectedTenantRoute = ({ children }) => {
  const { isAuthenticated, loading } = useTenant();
  const { tenantSlug } = useParams();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to={`/tenant/${tenantSlug}/login`} replace />;
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    // Brand Guardian: If not in a tenant-specific route, reset to Fluxo Emblem
    if (!location.pathname.startsWith('/tenant/')) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'icon';
      link.href = 'https://i.ibb.co/nMVVknHc/Emblema.png';
      
      if (!document.querySelector("link[rel~='icon']")) {
        document.getElementsByTagName('head')[0].appendChild(link);
      }
    }
  }, [location]);

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Tenant Portal Routes */}
      <Route path="/tenant/:tenantSlug/*" element={<TenantRouteWrapper><TenantRoutes /></TenantRouteWrapper>} />
      
      {/* Protected admin routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="clients" element={<TenantsPage />} />
        <Route path="bots" element={<BotsPage />} />
        <Route path="master-ai" element={<MasterAIPage />} />
        <Route path="conversations" element={<ConversationsPage />} />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="whatsapp" element={<WhatsAppConfigPage />} />
        <Route path="prospectos" element={<ProspectosPage />} />
        <Route path="factory" element={<BrainFactoryPage />} />
        <Route path="monitoring" element={<MonitoringPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="engine-structure" element={<DataEnginePage />} />
        <Route path="blueprint-studio" element={<BlueprintStudioPage />} />
      </Route>
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

import TenantConfigPage from "@/pages/TenantConfigPage";
import VentasProPage from "@/pages/VentasProPage";
import ClientesCRMPage from "@/pages/ClientesCRMPage";
import CitasProPage from "@/pages/CitasProPage";
import TicketsProPage from "@/pages/TicketsProPage";
import CatalogosProPage from "@/pages/CatalogosProPage";
import VacantesProPage from "@/pages/VacantesProPage";
import PostulacionesProPage from "@/pages/PostulacionesProPage";
import TenantReportsPage from "@/pages/TenantReportsPage";

// Tenant specific routes
function TenantRoutes() {
  const { tenantSlug } = useParams();
  
  return (
    <Routes>
      <Route path="login" element={<TenantLoginPage />} />
      <Route 
        path="/*" 
        element={
          <ProtectedTenantRoute>
            <TenantDashboardLayout />
          </ProtectedTenantRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<TenantDashboardPage />} />
        <Route path="ventas" element={<VentasProPage />} />
        <Route path="clientes" element={<ClientesCRMPage />} />
        <Route path="citas" element={<CitasProPage />} />
        <Route path="tickets" element={<TicketsProPage />} />
        <Route path="catalogos" element={<CatalogosProPage />} />
        <Route path="vacantes" element={<VacantesProPage />} />
        <Route path="postulaciones" element={<PostulacionesProPage />} />
        <Route path="reportes" element={<TenantReportsPage />} />
        <Route path="engine" element={<DataEnginePage />} />
        <Route path="engine-structure" element={<DataEnginePage />} />
        <Route path="config" element={<TenantConfigPage />} />
      </Route>
    </Routes>
  );
}


function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              background: 'hsl(240 10% 3.9%)',
              border: '1px solid hsl(240 3.7% 15.9%)',
              color: 'hsl(0 0% 98%)'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
